import { useEffect, useMemo, useRef, useState } from "react";
import { FormProvider, useForm, useFormContext, useWatch } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import type {
  CreatedTrip,
  PatientLookupResponse,
  SessionUser,
  TripCreateRecurringResponse,
  TripCreateRequest,
} from "@mtform/shared";
import { useMutation } from "@tanstack/react-query";
import { AlertTriangle, ArrowLeft, Repeat, RotateCcw, Send } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/auth/AuthContext";
import { ApiError, api } from "@/lib/api";
import { clearDraft, hasDraft, loadDraft, saveDraft } from "@/lib/draft";
import { useDraftAutoSave } from "@/hooks/useDraftAutoSave";
import { usePatientLookup } from "@/hooks/usePatientLookup";
import { useHasPermission } from "@/auth/usePermissions";
import {
  formatUsPhone,
  isSameAddressLine,
  normalizeDateForPayload,
} from "@/lib/format";
import {
  afterRebook,
  formatRecurringDate,
  holdDates,
  markUnconfirmed,
  type UnconfirmedDates,
} from "@/lib/recurrence";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Spinner } from "@/components/ui/spinner";
import { useConfirm } from "@/components/ui/confirm-dialog";
import { DraftBanner } from "@/components/shell/DraftBanner";
import { Stepper, type StepperStep } from "@/components/shell/Stepper";
import { PickupSection } from "./PickupSection";
import { DropoffSection } from "./DropoffSection";
import { ReturnTripSection } from "./ReturnTripSection";
import { MultiLegSection } from "./MultiLegSection";
import { RequesterSection } from "./RequesterSection";
import { SuccessScreen } from "./SuccessScreen";
import { RecurringSuccessScreen } from "./RecurringSuccessScreen";
import { BookingInProgress } from "./BookingInProgress";
import { useDelayedFlag } from "@/hooks/useDelayedFlag";
import { PatientLookupProvider } from "./PatientLookupContext";
import {
  DuplicateDateWarning,
  useTripDateCollisions,
} from "./DuplicateDateGate";
import { PatientLookupStep } from "./steps/PatientLookupStep";
import { PatientDetailsStep } from "./steps/PatientDetailsStep";
import { TripTypeStep } from "./steps/TripTypeStep";
import { TransportTypeStep } from "./steps/TransportTypeStep";
import { TransportDateStep } from "./steps/TransportDateStep";
import { TripSummary } from "./TripSummary";
import { TripSummaryRail } from "./TripSummaryRail";
import { WizardCard } from "./WizardCard";
import {
  buildStepList,
  displayIndex,
  fieldsOf,
  firstProblem,
  nextStep,
  STEP_TITLE,
  statusFor,
  type StepKey,
} from "./wizard";
import {
  toTripCreateRequest,
  tripFormDefaults,
  tripFormSchema,
  type TripFormValues,
} from "./schema";

type FoundPatient = Extract<PatientLookupResponse, { found: true }>["patient"];

/** Smooth scrolling unless the user asked the OS for reduced motion —
 *  then jump instantly instead of animating across the whole page. */
function scrollBehavior(): ScrollBehavior {
  return window.matchMedia?.("(prefers-reduced-motion: reduce)").matches
    ? "auto"
    : "smooth";
}

// =============================================================================
// TripForm
//
// Step-by-step wizard. Each section of the form is its own card; only the
// active card shows full inputs while the rest collapse into one-line
// summaries with an Edit button. The patient identifier (MRN + DOB) is
// locked once the user advances past the lookup step — to change it,
// staff use the "Edit" affordance which clears the form and bounces back
// to step 1, so prefilled values from a stale match never linger.
//
// The confirm step ("is this the right patient?") is conditional on a
// returning-patient match — for new patients we skip straight from
// lookup to the details step.
// =============================================================================

export function TripForm() {
  const [draftPromptOpen, setDraftPromptOpen] = useState(() => hasDraft());
  // Duplicate-date acknowledgement, shared by the transport-date step and the
  // review step so the booker answers once. Holds the collision key they
  // agreed to; any change to the colliding trips re-arms both gates.
  const [duplicateAckKey, setDuplicateAckKey] = useState("");
  const [createdTrips, setCreatedTrips] = useState<CreatedTrip[] | null>(null);
  const [recurringResult, setRecurringResult] =
    useState<TripCreateRecurringResponse | null>(null);
  // The result on screen includes a re-book, which sends its own email.
  const [rebooked, setRebooked] = useState(false);
  const [submitError, setSubmitError] = useState<string>("");
  const [currentStep, setCurrentStep] = useState<StepKey>("lookup");
  // Track which steps the user has walked through so that clicking Edit on an
  // earlier card doesn't make subsequent answered cards disappear — they
  // stay visible as collapsed summaries until the user explicitly resets.
  const [answeredSteps, setAnsweredSteps] = useState<ReadonlySet<StepKey>>(
    () => new Set<StepKey>(),
  );
  const confirm = useConfirm();
  const auth = useAuth();
  const me = auth.state.status === "authenticated" ? auth.state.user : null;

  // Bake the logged-in user's profile values into the form defaults so
  // *every* reset path (initial mount, draft restore, "wrong patient",
  // successful submit → restart) starts with Requester pre-filled. The
  // older effect-only approach inside RequesterSection only fired when
  // the `me` identity changed, so any methods.reset(tripFormDefaults)
  // after auth had loaded would silently wipe the requester values and
  // never refill them. See: draft-restore jumps straight to step 8.
  // Whether this user may book inside the minimum lead time (with a reason).
  // Carried in form state because the zod schema needs it to tell "you can't
  // book this soon" apart from "you can, but say why", and zod has no access
  // to the auth context. Folded into the defaults rather than set by an
  // effect so it survives every methods.reset(meDefaults) below.
  const canBookShortNotice = useHasPermission("bookShortNotice");

  const meDefaults = useMemo<TripFormValues>(
    () => ({ ...buildDefaultsFromUser(me), canBookShortNotice }),
    // We only re-derive when account identity / contact fields change.
    // The full `me` object is recreated on every render, so we depend on
    // primitives to keep this stable.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [me?.id, me?.firstName, me?.lastName, me?.phone, me?.email, canBookShortNotice],
  );

  const methods = useForm<TripFormValues>({
    defaultValues: meDefaults,
    resolver: zodResolver(tripFormSchema) as never,
    mode: "onBlur",
    shouldFocusError: true,
  });

  // When auth finishes loading *after* the form has already mounted with
  // empty defaults, retro-actively fill the three requester fields from
  // the profile — but only the empty ones, so we don't clobber anything
  // the user has typed in the meantime. The ref keys on userId so this
  // only fires once per signed-in account, never on subsequent renders.
  const seededRequesterForUserRef = useRef<string | null>(null);
  useEffect(() => {
    if (!me) return;
    if (seededRequesterForUserRef.current === me.id) return;
    seededRequesterForUserRef.current = me.id;
    const fullName = `${me.firstName ?? ""} ${me.lastName ?? ""}`.trim();
    if (fullName && !String(methods.getValues("requestedBy") ?? "").trim()) {
      methods.setValue("requestedBy", fullName, { shouldDirty: false });
    }
    if (me.phone && !String(methods.getValues("callbackPhone") ?? "").trim()) {
      methods.setValue("callbackPhone", formatUsPhone(me.phone), {
        shouldDirty: false,
      });
    }
    if (
      me.email &&
      !String(methods.getValues("requesterEmail") ?? "").trim()
    ) {
      methods.setValue("requesterEmail", me.email, { shouldDirty: false });
    }
  }, [me, methods]);

  const tripType = useWatch({ control: methods.control, name: "tripType" }) ?? "";
  const mrnWatch = useWatch({ control: methods.control, name: "mrn" });
  const dobWatch = useWatch({ control: methods.control, name: "dob" });

  const lookup = usePatientLookup({
    mrn: mrnWatch ?? "",
    dob: dobWatch ?? "",
    paused: Boolean(createdTrips) || Boolean(recurringResult),
  });

  // ---- Auto-save draft ----------------------------------------------------
  const watchedValues = methods.watch();
  useDraftAutoSave(
    watchedValues,
    !createdTrips &&
      !recurringResult &&
      !draftPromptOpen &&
      methods.formState.isDirty,
  );

  // ---- Step list (recomputes when conditional gates change) --------------
  const steps = useMemo(
    () => buildStepList({ tripType: String(tripType ?? "") }),
    [tripType],
  );

  // If the active step disappeared from the list (e.g. user changed
  // tripType from round_trip back to one_way after passing the return
  // step), fall back to the first downstream step still in the list.
  useEffect(() => {
    if (steps.includes(currentStep)) return;
    // Find the closest preceding step that's still present, then advance
    // by one — keeps the user where they were rather than throwing them
    // back to the start.
    setCurrentStep("lookup");
  }, [steps, currentStep]);

  // Smoothly scroll the newly-active step into view AND move keyboard
  // focus to its heading so screen-reader users hear the new step's
  // title when the wizard advances. Skipping the very first step
  // (lookup) because the page already starts there and we don't want a
  // jarring scroll on initial mount.
  useEffect(() => {
    if (currentStep === "lookup") return;
    const el = document.querySelector('[data-wizard-active="true"]');
    if (!el || !("scrollIntoView" in el)) return;
    requestAnimationFrame(() => {
      (el as HTMLElement).scrollIntoView({
        behavior: scrollBehavior(),
        block: "center",
      });
      // tabindex=-1 + focus() is the standard "jump-target" pattern —
      // the heading isn't in the natural tab order, but programmatic
      // focus triggers the screen reader to announce its text.
      const heading = el.querySelector("h2") as HTMLElement | null;
      if (heading) {
        if (!heading.hasAttribute("tabindex")) {
          heading.setAttribute("tabindex", "-1");
        }
        heading.focus({ preventScroll: true });
      }
    });
  }, [currentStep]);

  // ---- Auto-prefill on returning patient ----------------------------------
  const prefilledForRef = useRef<string | null>(null);
  useEffect(() => {
    if (lookup.status !== "found" || !lookup.patient) return;
    const isoDob = normalizeDateForPayload(dobWatch ?? "");
    const trimmedMrn = String(mrnWatch ?? "").trim().toLowerCase();
    if (!trimmedMrn || !isoDob) return;
    const key = `${trimmedMrn}|${isoDob}`;
    if (prefilledForRef.current === key) return;
    prefilledForRef.current = key;
    applyPrefill(lookup.patient);
    // applyPrefill is a stable closure over methods + lookup.patient.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lookup.status, lookup.patient, mrnWatch, dobWatch]);

  function applyPrefill(p: FoundPatient) {
    const fillStringIfEmpty = (
      name:
        | "firstName"
        | "lastName"
        | "department"
        | "costCenter"
        | "contactPhone"
        | "pickupAddress"
        | "pickupCityZip"
        | "pickupLocation"
        | "destinationAddress"
        | "destinationCityZip"
        | "requestedBy"
        | "callbackPhone"
        | "requesterEmail",
      value: string,
    ) => {
      const current = String(methods.getValues(name) ?? "").trim();
      if (!current) {
        methods.setValue(name, value, { shouldDirty: true, shouldTouch: true });
      }
    };
    const fillPlaceIfEmpty = (
      name: "pickupPlace" | "destinationPlace",
      value: TripFormValues["pickupPlace"],
    ) => {
      const current = methods.getValues(name);
      if (!current) {
        methods.setValue(name, value, { shouldDirty: true });
      }
    };

    fillStringIfEmpty("firstName", p.firstName);
    fillStringIfEmpty("lastName", p.lastName);
    if (p.department) fillStringIfEmpty("department", p.department);
    if (p.costCenter) fillStringIfEmpty("costCenter", p.costCenter);
    if (p.contactPhone)
      fillStringIfEmpty("contactPhone", formatUsPhone(p.contactPhone));

    if (p.savedPickups.length === 1) {
      const s = p.savedPickups[0]!;
      // Strip any duplicated cityZip / ", USA" tail that pre-fix
      // bookings baked into the saved address — otherwise we feed the
      // buggy full string straight back into the form on the next
      // booking and triplink TMS drops the stops display again.
      const cleanAddress = cleanSavedAddress(s.address, s.cityZip);
      fillStringIfEmpty("pickupAddress", cleanAddress);
      fillStringIfEmpty("pickupCityZip", s.cityZip);
      if (s.location) fillStringIfEmpty("pickupLocation", s.location);
      fillPlaceIfEmpty("pickupPlace", {
        lat: s.lat,
        lon: s.lon,
        buildingNumber: "",
        line1: "",
        city: "",
        state: "",
        postalCode: "",
        formatted: s.address,
      });
    }

    if (p.savedDestinations.length === 1) {
      const s = p.savedDestinations[0]!;
      const cleanAddress = cleanSavedAddress(s.address, s.cityZip);
      fillStringIfEmpty("destinationAddress", cleanAddress);
      fillStringIfEmpty("destinationCityZip", s.cityZip);
      fillPlaceIfEmpty("destinationPlace", {
        lat: s.lat,
        lon: s.lon,
        buildingNumber: "",
        line1: "",
        city: "",
        state: "",
        postalCode: "",
        formatted: s.address,
      });
    }

    if (p.savedRequesters.length === 1) {
      const s = p.savedRequesters[0]!;
      fillStringIfEmpty("requestedBy", s.requestedBy);
      if (s.callbackPhone)
        fillStringIfEmpty("callbackPhone", formatUsPhone(s.callbackPhone));
      if (s.requesterEmail) fillStringIfEmpty("requesterEmail", s.requesterEmail);
    } else if (p.savedCallbackPhones.length === 1) {
      const s = p.savedCallbackPhones[0]!;
      fillStringIfEmpty("callbackPhone", formatUsPhone(s.phone));
    }
  }

  // ---- Stepper headline ---------------------------------------------------
  const stepperSteps = useMemo<StepperStep[]>(() => {
    const groups: Array<{ label: string; keys: StepKey[] }> = [
      { label: "Patient", keys: ["lookup", "details"] },
      {
        label: "Trip details",
        keys: [
          "tripType",
          "transportType",
          "transportDate",
          "pickup",
          "dropoff",
          "return",
          "multiLeg",
          "requester",
        ],
      },
      { label: "Review", keys: ["submit"] },
    ];
    const currentIdx = groups.findIndex((g) => g.keys.includes(currentStep));
    return groups.map((g, idx) => ({
      key: g.label,
      label: g.label,
      state:
        idx < currentIdx ? "complete" : idx === currentIdx ? "active" : "pending",
    }));
  }, [currentStep]);

  // ---- beforeunload guard ------------------------------------------------
  useEffect(() => {
    const handler = (event: BeforeUnloadEvent) => {
      if (methods.formState.isDirty && !createdTrips && !recurringResult) {
        event.preventDefault();
        event.returnValue = "";
      }
    };
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, [methods.formState.isDirty, createdTrips, recurringResult]);

  // ---- Submit mutations --------------------------------------------------
  // The base trip used for the most recent recurring submit, kept so the
  // "re-book failed dates" button can resend just the failures without
  // the form (which we reset on success).
  const lastRecurringTripRef = useRef<TripCreateRequest | null>(null);

  // When the *booking work* started — set the moment the patient is
  // confirmed (advancing past the lookup step), or when a saved draft
  // is resumed. Deliberately NOT set on mount: time spent landing on
  // the form, entering MRN/DOB, and waiting on the lookup shouldn't
  // count toward "time to book". Null until armed. Powers the "typical
  // time to book" dashboard metric (formDurationMs on single creates).
  const formStartRef = useRef<number | null>(null);

  // Counts the booker's resets, so a booking that was still out when they
  // reset the form, and then fails, leaves the new form alone. (One that
  // succeeds still shows it was booked.)
  const resetsRef = useRef(0);

  function resetFormAfterSubmit() {
    methods.reset(meDefaults);
    clearDraft();
    setCurrentStep("lookup");
    setAnsweredSteps(new Set<StepKey>());
    prefilledForRef.current = null;
    formStartRef.current = null;
    window.scrollTo({ top: 0, behavior: scrollBehavior() });
  }

  function describeSubmitError(err: unknown): string {
    // Keep the two genuinely-actionable messages specific; everything else —
    // including an unreachable dispatch upstream — gets a friendly,
    // vendor-free generic line (never surface "TripLink" or a raw error).
    if (err instanceof ApiError) {
      if (err.code === "INVALID_INPUT")
        return "Some fields didn't pass validation. Use the Edit buttons above to fix them, then try again.";
      if (err.code === "RATE_LIMITED")
        return "We received many requests just now. Please wait a moment and retry.";
      // Dispatch couldn't build a route between the addresses as entered —
      // usually a drop-off picked from autocomplete that resolved to a POI with
      // no street address (or the wrong city entirely). Retrying is futile, so
      // say what to change instead of the generic line: a booker hit this on
      // 2026-07-30 and retried five times against an unroutable destination.
      if (err.code === "UNROUTABLE_ADDRESS")
        return "We couldn't calculate a route between those addresses. Check the pickup and drop-off, and pick a full street address from the suggestions rather than a place name.";
      // Too little notice, when the time went stale while the form was open:
      // the server says how much notice is needed and what to do.
      // Dispatch refused a later leg: the server says what to check, and what
      // to do if the first leg is booked.
      if (
        err.code === "LEAD_TIME_TOO_SHORT" ||
        err.code === "SHORT_NOTICE_REASON_REQUIRED" ||
        err.code === "LATER_LEG_REFUSED"
      )
        return err.message;
    }
    return "Oops, something went wrong. Please try again.";
  }

  const submitMutation = useMutation({
    mutationFn: (payload: TripCreateRequest) => api.trip.create(payload),
    onSuccess: (response) => {
      setCreatedTrips(response.trips);
      resetFormAfterSubmit();
    },
  });

  // Errors are handled per call: a submit and a re-book each say something
  // different.
  const recurringMutation = useMutation({
    mutationFn: (input: { trip: TripCreateRequest; dates: string[] }) =>
      api.trip.createRecurring(input),
    onSuccess: (response) => {
      setRecurringResult(response);
      setRebooked(false);
      resetFormAfterSubmit();
    },
  });

  const isSubmitting = submitMutation.isPending || recurringMutation.isPending;
  // Show the booking overlay only when the submit is genuinely slow — a
  // normal instant booking never flashes it (see useDelayedFlag).
  const showBookingOverlay = useDelayedFlag(isSubmitting);

  // Live payload computed from the current form values so the inline
  // review on the submit step reflects edits without a roundtrip. Null
  // while an answer fails the full check, with `problem` naming the first
  // one: each step checks only its own answers, and some go stale after
  // (a pickup that became too soon, a draft restored the next day).
  const liveValues = methods.watch();
  const liveCheck = useMemo<{
    payload: TripCreateRequest | null;
    problem: ReturnType<typeof firstProblem>;
  }>(() => {
    const parsed = tripFormSchema.safeParse(liveValues);
    if (parsed.success) return { payload: toTripCreateRequest(parsed.data), problem: null };
    return { payload: null, problem: firstProblem(steps, parsed.error.issues) };
  }, [liveValues, steps]);
  const livePayload = liveCheck.payload;

  async function handleSubmit() {
    if (!livePayload) {
      void methods.trigger();
      setSubmitError(
        "Some fields are still missing. Use the Edit buttons above to fix them.",
      );
      return;
    }
    setSubmitError("");

    // Guard the classic booking slip: the appointment address typed into
    // BOTH pickup and drop-off, leaving out the patient's residence. A soft
    // confirm (not a hard block) - the rare legitimate same-address trip can
    // proceed, but the common mistake gets one chance to be caught.
    if (
      isSameAddressLine(
        livePayload.pickupAddress,
        livePayload.pickupCityZip ?? "",
        livePayload.destinationAddress,
        livePayload.destinationCityZip ?? "",
      )
    ) {
      const proceed = await confirm({
        title: "Pickup and drop-off are the same address",
        description:
          "This trip has the same pickup and drop-off. If the patient is going to or from home, the residence should be in one of them. Submit anyway?",
        confirmText: "Yes, submit",
        cancelText: "Let me fix it",
      });
      if (!proceed) return;
    }

    // Recurring path: fan the base trip out across the picked dates in a
    // single request. The server stamps each date onto an otherwise
    // identical payload, so `livePayload` (which carries the first date)
    // is exactly the right base to send.
    const repeatEnabled = Boolean(liveValues.repeatEnabled);
    const repeatDates = Array.isArray(liveValues.repeatDates)
      ? (liveValues.repeatDates as string[])
      : [];
    const recurring = repeatEnabled && repeatDates.length > 0;

    // Until a clear answer comes back, the dates being sent may book, a
    // single trip's as much as a series'. They're held in the form, so the
    // draft keeps them through a reload or a trip to Booked Trips, and Submit
    // for any of them waits until they've been checked. Success resets the
    // form; only a clear no lets them go.
    const held = methods.getValues("unconfirmed") ?? NONE_HELD;
    methods.setValue(
      "unconfirmed",
      holdDates(held, recurring ? repeatDates : [livePayload.transportDate]),
      { shouldDirty: true },
    );
    // Saved now, not after the auto-save's pause: the booker may leave
    // while the booking is still out. This booking replaces any draft the
    // banner was still offering, as a successful one would, and the
    // auto-save keeps it up to date from here.
    saveDraft(methods.getValues());
    setDraftPromptOpen(false);
    const resets = resetsRef.current;
    const onError = (err: unknown) => {
      if (resetsRef.current !== resets) return;
      // Dispatch refused a later leg, so the first may already be booked:
      // the date stays held, with what the portal said, for the box.
      if (err instanceof ApiError && err.code === "LATER_LEG_REFUSED") {
        const now = methods.getValues("unconfirmed") ?? NONE_HELD;
        methods.setValue(
          "unconfirmed",
          { ...now, notes: { ...now.notes, [livePayload.transportDate]: describeSubmitError(err) } },
          { shouldDirty: true },
        );
        return;
      }
      // With no clear answer, the box above Submit says what to do.
      if (!isClearRefusal(err)) return;
      methods.setValue("unconfirmed", held, { shouldDirty: true });
      // Saved now too: a 401 signs the booker out, and leaving unmounts the
      // form before the auto-save's pause is up.
      saveDraft(methods.getValues());
      setSubmitError(describeSubmitError(err));
    };

    if (recurring) {
      lastRecurringTripRef.current = livePayload;
      recurringMutation.mutate({ trip: livePayload, dates: repeatDates }, { onError });
      return;
    }

    // Attach how long this booking took — measured from patient-confirm
    // to submit, clamped to a sane ceiling; the server filters to
    // [2s, 30m] for the metric. Only sent when the clock was armed
    // (a confirmed patient or a resumed draft).
    const start = formStartRef.current;
    const formDurationMs =
      start != null ? Math.min(Math.max(0, Date.now() - start), 86_400_000) : undefined;
    submitMutation.mutate(
      formDurationMs != null
        ? { ...livePayload, formDurationMs }
        : livePayload,
      { onError },
    );
  }

  // Re-book only the dates that failed in a recurring batch. Reuses the
  // base trip captured at the original submit.
  function handleRetryFailed(dates: string[]) {
    const base = lastRecurringTripRef.current;
    if (!base || dates.length === 0) return;
    const previous = recurringResult;
    setSubmitError("");
    setRecurringResult(null);
    recurringMutation.mutate(
      { trip: base, dates },
      {
        // The whole series stays on screen, the re-sent dates updated.
        onSuccess: (response) => {
          if (previous) setRecurringResult(afterRebook(previous, response));
          setRebooked(true);
        },
        // The form behind the result was reset after the first submit, so an
        // error there would show nowhere: bring the result back and say what
        // went wrong. Only a clear no (see isClearRefusal) leaves its dates
        // re-bookable. A re-book that got no answer, or that failed on the
        // server, may have booked them, so they come back as not confirmed.
        onError: (err) => {
          const refused = isClearRefusal(err);
          setRecurringResult(previous && !refused ? markUnconfirmed(previous, dates) : previous);
          toast.error(
            refused
              ? describeSubmitError(err)
              : dates.length === 1
                ? "The re-book didn't finish, so that date may already be booked. Check Booked Trips before booking it again."
                : "The re-book didn't finish, so those dates may already be booked. Check Booked Trips before booking them again.",
          );
        },
      },
    );
  }

  // ---- Reset patient lookup (clears form + bounces to lookup step) --------
  function handleResetPatient() {
    resetsRef.current += 1;
    methods.reset(meDefaults);
    clearDraft();
    setSubmitError("");
    prefilledForRef.current = null;
    formStartRef.current = null;
    setCurrentStep("lookup");
    setAnsweredSteps(new Set<StepKey>());
    window.scrollTo({ top: 0, behavior: scrollBehavior() });
  }

  function goToStep(step: StepKey) {
    setCurrentStep(step);
  }

  function advance(from: StepKey) {
    // Start the "time to book" clock the moment the patient is confirmed
    // (leaving the lookup step). Only the first confirmation arms it, so
    // bouncing back and forth between steps doesn't reset the timer.
    if (from === "lookup" && formStartRef.current == null) {
      formStartRef.current = Date.now();
    }
    setAnsweredSteps((prev) => {
      const next = new Set(prev);
      next.add(from);
      return next;
    });
    setCurrentStep(nextStep(steps, from));
  }

  // ---- Draft restore ------------------------------------------------------
  function handleRestoreDraft() {
    const draft = loadDraft<TripFormValues>();
    if (draft) {
      // A resumed draft already has the patient confirmed from a prior
      // session, so start the clock now — measuring this session's
      // finish-and-submit work.
      formStartRef.current = Date.now();
      // Merge over me-seeded defaults so any requester fields the draft
      // didn't explicitly set still fall back to the user's profile.
      // canBookShortNotice last: a saved draft carries whatever the flag was
      // when it was written, and a permission granted or revoked since then
      // must win over a stale copy in localStorage.
      // Against the blank form's defaults, so the restored form counts as
      // changed: the draft is then saved on every change, including one back
      // to how it was restored (a re-sent date losing its tick).
      methods.reset(
        { ...meDefaults, ...draft, canBookShortNotice },
        { keepDefaultValues: true },
      );
      // Restored drafts already have the patient identifier — drop the
      // user straight into the wizard at the requester step so they can
      // verify and submit. They can still hit Edit on any earlier card.
      setCurrentStep("requester");
      // Mark every step preceding the requester step as answered so the
      // collapsed summaries show their values right away rather than
      // requiring the user to walk back to "complete" them.
      const draftSteps = buildStepList({
        tripType: String(draft.tripType ?? ""),
      });
      const requesterIdx = draftSteps.indexOf("requester");
      if (requesterIdx > 0) {
        setAnsweredSteps(new Set(draftSteps.slice(0, requesterIdx)));
      }
    }
    setDraftPromptOpen(false);
  }

  function handleDiscardDraft() {
    clearDraft();
    setDraftPromptOpen(false);
  }

  if (recurringResult) {
    return (
      <RecurringSuccessScreen
        result={recurringResult}
        rebooked={rebooked}
        onRetryFailed={handleRetryFailed}
        onCreateAnother={() => {
          setRecurringResult(null);
          lastRecurringTripRef.current = null;
        }}
      />
    );
  }

  if (createdTrips) {
    return (
      <SuccessScreen
        trips={createdTrips}
        onCreateAnother={() => {
          setCreatedTrips(null);
        }}
      />
    );
  }

  // ---- Status helpers used by every step ---------------------------------
  const stat = (key: StepKey) =>
    statusFor(steps, currentStep, answeredSteps, key);
  const idx = (key: StepKey) => displayIndex(steps, key);

  return (
    <FormProvider {...methods}>
      <PatientLookupProvider value={lookup}>
        <BookingInProgress active={showBookingOverlay} />
        <div className="grid gap-8 lg:grid-cols-[minmax(0,1fr)_340px] lg:items-start xl:grid-cols-[minmax(0,1fr)_360px] xl:gap-10">
          <div className="min-w-0">
            <header className="mb-6 sm:mb-8">
              <h1 className="text-3xl font-extrabold tracking-tight text-foreground sm:text-[2.5rem] sm:leading-[1.1]">
                New trip request
              </h1>
              <p className="mt-2 max-w-[60ch] text-[15px] text-muted-foreground">
                Book transport for a Mount Sinai patient. Your progress saves
                automatically as you go.
              </p>
            </header>
            {draftPromptOpen ? (
              <div className="mb-4">
                <DraftBanner onRestore={handleRestoreDraft} onDiscard={handleDiscardDraft} />
              </div>
            ) : null}
            {/* The live rail replaces the stepper on wide screens. */}
            <div className="lg:hidden">
              <Stepper steps={stepperSteps} />
            </div>
            {/* `grid-cols-[minmax(0,1fr)]` is the canonical fix for the
                grid/flex "intrinsic min-width: auto" overflow trap — without
                it, any descendant whose own min-width exceeds the viewport
                (chip groups with `min-w-[9rem]`, side-by-side buttons, a
                long unbreakable address string) would push every step card
                wider than the phone screen. The `[&>*]:min-w-0` belt-and-
                suspenders does the same for any nested grids further down
                the tree. */}
            <form
              noValidate
              onSubmit={(e) => e.preventDefault()}
              className="grid grid-cols-[minmax(0,1fr)] gap-3 [&>*]:min-w-0"
            >
              <PatientLookupStep
                index={idx("lookup")}
                status={stat("lookup")}
                onContinue={() => advance("lookup")}
                onWrongPatient={handleResetPatient}
                onChangeIdentifier={async () => {
                  // Editing MRN/DOB after the user has passed through has
                  // to wipe everything we prefilled — otherwise stale
                  // name / department / saved-history would silently
                  // belong to the previous patient. Confirm so it isn't
                  // a misclick.
                  const proceed = await confirm({
                    title: "Reset the form?",
                    description:
                      "Editing the patient identifier will clear every field below it. This can't be undone.",
                    confirmText: "Yes, reset",
                    cancelText: "Keep editing",
                    destructive: true,
                  });
                  if (proceed) handleResetPatient();
                }}
              />
              <PatientDetailsStep
                index={idx("details")}
                status={stat("details")}
                onContinue={() => advance("details")}
                onEdit={() => goToStep("details")}
              />
              <TripTypeStep
                index={idx("tripType")}
                status={stat("tripType")}
                onContinue={() => advance("tripType")}
                onEdit={() => goToStep("tripType")}
              />
              <TransportTypeStep
                index={idx("transportType")}
                status={stat("transportType")}
                onContinue={() => advance("transportType")}
                onEdit={() => goToStep("transportType")}
              />
              <TransportDateStep
                acknowledgedKey={duplicateAckKey}
                onAcknowledge={setDuplicateAckKey}
                index={idx("transportDate")}
                status={stat("transportDate")}
                onContinue={() => advance("transportDate")}
                onEdit={() => goToStep("transportDate")}
              />
              <PickupSection
                index={idx("pickup")}
                status={stat("pickup")}
                onContinue={() => advance("pickup")}
                onEdit={() => goToStep("pickup")}
              />
              <DropoffSection
                index={idx("dropoff")}
                status={stat("dropoff")}
                onContinue={() => advance("dropoff")}
                onEdit={() => goToStep("dropoff")}
              />
              {steps.includes("return") ? (
                <ReturnTripSection
                  index={idx("return")}
                  status={stat("return")}
                  onContinue={() => advance("return")}
                  onEdit={() => goToStep("return")}
                />
              ) : null}
              {steps.includes("multiLeg") ? (
                <MultiLegSection
                  index={idx("multiLeg")}
                  status={stat("multiLeg")}
                  onContinue={() => advance("multiLeg")}
                  onEdit={() => goToStep("multiLeg")}
                />
              ) : null}
              <RequesterSection
                index={idx("requester")}
                status={stat("requester")}
                onContinue={() => advance("requester")}
                onEdit={() => goToStep("requester")}
              />
              <SubmitStep
                acknowledgedKey={duplicateAckKey}
                onAcknowledge={setDuplicateAckKey}
                index={idx("submit")}
                active={currentStep === "submit"}
                submitting={isSubmitting}
                submitError={submitError}
                payload={livePayload}
                problem={liveCheck.problem}
                onFix={(step) => {
                  // Mark the step's failing answers, so it opens showing why.
                  void methods.trigger(fieldsOf(step));
                  goToStep(step);
                }}
                repeatDates={
                  liveValues.repeatEnabled && Array.isArray(liveValues.repeatDates)
                    ? (liveValues.repeatDates as string[])
                    : []
                }
                onSubmit={handleSubmit}
                onBack={() => setCurrentStep("requester")}
                onResetAll={handleResetPatient}
              />
            </form>
          </div>
          <aside aria-label="Trip summary" className="hidden lg:sticky lg:top-24 lg:block">
            <TripSummaryRail steps={steps} currentStep={currentStep} answered={answeredSteps} />
          </aside>
        </div>
      </PatientLookupProvider>
    </FormProvider>
  );
}

const NONE_HELD: UnconfirmedDates = { dates: [], checked: [], notes: {} };

/** A clear no from the portal (its own 4xx answer, or its "briefly
 *  unavailable", sent before any booking starts) means nothing was booked.
 *  No answer, an error on the server, an answer that isn't the portal's (a
 *  proxy's page), or a refused later leg may come after something booked. */
function isClearRefusal(err: unknown): boolean {
  return (
    err instanceof ApiError &&
    (err.code === "SERVICE_UNAVAILABLE" ||
      (err.code !== "BAD_RESPONSE" &&
        err.code !== "LATER_LEG_REFUSED" &&
        err.status >= 400 &&
        err.status < 500))
  );
}

// =============================================================================
// buildDefaultsFromUser
//
// Returns a fresh TripFormValues object with the same shape as
// `tripFormDefaults` but with the three Requester fields pre-seeded
// from the currently signed-in user's account profile (name, phone,
// email). Called from every reset path so every form lifecycle starts
// with Requester pre-filled instead of relying on a downstream effect
// to retro-actively patch the values in.
// =============================================================================

function buildDefaultsFromUser(user: SessionUser | null): TripFormValues {
  if (!user) return tripFormDefaults;
  const fullName = `${user.firstName ?? ""} ${user.lastName ?? ""}`.trim();
  return {
    ...tripFormDefaults,
    requestedBy: fullName,
    callbackPhone: user.phone ? formatUsPhone(user.phone) : "",
    requesterEmail: user.email ?? "",
  };
}

/** Sanitize a saved-history address string before refilling. Older
 *  bookings (created before we switched to street-only `pickupAddress`)
 *  baked the full Google formatted address into the column — city,
 *  state, ZIP, and a trailing ", USA". Feeding that straight back into
 *  the form makes the next booking re-trigger the same TMS-display
 *  bug. Strip any duplicated cityZip / country suffix so the saved
 *  prefill matches the new shape. */
function cleanSavedAddress(address: string, cityZip: string): string {
  let cleaned = String(address ?? "").trim();
  // Drop trailing ", USA" / ", United States" first (covers both Google
  // formatted_address shapes).
  cleaned = cleaned
    .replace(/,\s*USA\s*$/iu, "")
    .replace(/,\s*United States\s*$/iu, "")
    .trim();
  // Then strip a trailing ", <cityZip>" if it duplicates the cityZip
  // column we're about to write to the paired pickupCityZip field.
  const cz = String(cityZip ?? "").trim();
  if (cz) {
    const escaped = cz.replace(/[.*+?^${}()|[\]\\]/gu, "\\$&");
    cleaned = cleaned.replace(new RegExp(`,\\s*${escaped}\\s*$`, "iu"), "").trim();
  }
  return cleaned || address;
}

// =============================================================================
// SubmitStep
//
// The final card. Renders the full sectioned trip summary inline (no
// modal) so staff can read every detail in place and submit with a
// single click. The cards above are kept as quick-scan summaries; this
// step is the authoritative read-out.
// =============================================================================

interface SubmitStepProps {
  index: number;
  active: boolean;
  /** Shared with the transport-date step — see DuplicateDateGate. */
  acknowledgedKey: string;
  onAcknowledge: (key: string) => void;
  submitting: boolean;
  submitError: string;
  payload: TripCreateRequest | null;
  /** Without a payload: the first answer that fails, and its step. */
  problem: ReturnType<typeof firstProblem>;
  onFix: (step: StepKey) => void;
  /** Non-empty when "Repeat this trip" is on — the full list of dates
   *  the batch will book. */
  repeatDates: string[];
  onSubmit: () => void;
  onBack: () => void;
  onResetAll: () => void;
}

function SubmitStep({
  index,
  active,
  acknowledgedKey,
  onAcknowledge,
  submitting,
  submitError,
  payload,
  problem,
  onFix,
  repeatDates,
  onSubmit,
  onBack,
  onResetAll,
}: SubmitStepProps) {
  const confirm = useConfirm();
  // Re-check here, not just on the date step. Restoring an auto-saved draft
  // jumps straight to the requester step with the date already marked
  // answered, so the date card renders a collapsed summary and its gate never
  // runs — the interrupted-booking-resumed-next-day case this feature exists
  // for. This is the last point before dispatch.
  const submitDates = useMemo(
    () =>
      repeatDates.length > 0
        ? repeatDates
        : payload?.transportDate
          ? [payload.transportDate]
          : [],
    [repeatDates, payload],
  );
  const { collisions, collisionKey } = useTripDateCollisions(submitDates);
  const duplicateBlocked =
    collisions.length > 0 && acknowledgedKey !== collisionKey;
  // Dates going out again after a submit that got no clear answer. A tick
  // covers exactly the dates it was given for: any change to them unticks
  // it, as with the duplicate-date check.
  const { setValue } = useFormContext<TripFormValues>();
  const held = useWatch<TripFormValues, "unconfirmed">({ name: "unconfirmed" }) ?? NONE_HELD;
  // From the form, not the payload: the box still shows when the payload
  // doesn't parse (a trip past the notice limit by the time it's checked, or
  // a draft restored the next morning).
  const tripType = useWatch<TripFormValues, "tripType">({ name: "tripType" });
  const transportDate = useWatch<TripFormValues, "transportDate">({ name: "transportDate" });
  const sending =
    repeatDates.length > 0
      ? repeatDates
      : [normalizeDateForPayload(transportDate ?? "")].filter(Boolean);
  const resent = sending.filter((d) => held.dates.includes(d));
  const ticked =
    resent.length === held.checked.length &&
    resent.every((d) => held.checked.includes(d));
  const unconfirmedBlocked = resent.length > 0 && !ticked;
  // What the portal said about this one trip's date, if that's all being sent.
  const note =
    resent.length === 1 && sending.length === 1 ? held.notes?.[resent[0]!] : undefined;
  // A submit that came back without a clear answer leaves Submit disabled:
  // take keyboard focus to the box that unlocks it.
  const holdCheckboxRef = useRef<HTMLButtonElement>(null);
  const wasSubmitting = useRef(submitting);
  useEffect(() => {
    if (wasSubmitting.current && !submitting && unconfirmedBlocked) {
      holdCheckboxRef.current?.focus();
    }
    wasSubmitting.current = submitting;
  }, [submitting, unconfirmedBlocked]);

  if (!active) {
    return <WizardCard index={index} title="Review & submit" status="pending" />;
  }

  const isRecurring = repeatDates.length > 0;

  return (
    <WizardCard
      index={index}
      title="Review & submit"
      description="Last chance to catch anything. Hit Submit when everything looks right."
      status="active"
    >
      {isRecurring ? (
        <div className="flex items-start gap-2.5 rounded-xl border border-primary/35 bg-primary/[0.06] px-3.5 py-2.5">
          <Repeat className="mt-0.5 h-4 w-4 shrink-0 text-primary" />
          <p className="text-sm text-foreground">
            <span className="font-semibold">
              Booking {repeatDates.length} trip
              {repeatDates.length === 1 ? "" : "s"}
            </span>{" "}
            <span className="text-muted-foreground">
              ({formatRecurringDate(repeatDates[0]!)} →{" "}
              {formatRecurringDate(repeatDates[repeatDates.length - 1]!)}). The
              details below apply to every date; each is its own independent
              trip.
            </span>
          </p>
        </div>
      ) : null}

      {payload ? (
        <TripSummary payload={payload} />
      ) : problem ? (
        // Submit stays disabled until this is fixed, so say what and where.
        // No role="alert": it would speak on every visit to Review, over the
        // step heading that takes focus and any "may already be booked" box.
        <div
          className="flex flex-wrap items-center justify-between gap-x-3 gap-y-2 rounded-2xl border border-amber-400/45 bg-amber-400/[0.08] px-3 py-3"
        >
          <div className="flex min-w-0 items-start gap-2">
            <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-amber-600 dark:text-amber-400" />
            <div className="min-w-0">
              <p className="text-sm font-semibold text-foreground">
                {STEP_TITLE[problem.step]} needs a fix
              </p>
              <p className="mt-0.5 text-xs text-muted-foreground">{problem.message}</p>
            </div>
          </div>
          <Button type="button" variant="secondary" size="sm" onClick={() => onFix(problem.step)}>
            Edit {STEP_TITLE[problem.step]}
          </Button>
        </div>
      ) : (
        <p className="rounded-xl border border-dashed border-border bg-foreground/[0.02] px-3 py-2 text-sm text-muted-foreground">
          A few fields are still missing — use the Edit buttons above to fix
          them, then come back here.
        </p>
      )}

      {collisions.length > 0 ? (
        <DuplicateDateWarning
          collisions={collisions}
          acknowledged={acknowledgedKey === collisionKey}
          onAcknowledge={(checked) => onAcknowledge(checked ? collisionKey : "")}
        />
      ) : null}

      {resent.length > 0 && !submitting ? (
        <div
          role="alert"
          className="rounded-2xl border border-amber-400/45 bg-amber-400/[0.08] px-3 py-3"
        >
          <div className="flex items-start gap-2">
            <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-amber-600 dark:text-amber-400" />
            <div className="min-w-0">
              <p className="text-sm font-semibold text-foreground">
                {resent.length < sending.length
                  ? `${resent.length} of these trips may already be booked`
                  : resent.length === 1
                    ? "This trip may already be booked"
                    : "These trips may already be booked"}
              </p>
              <p className="mt-0.5 text-xs text-muted-foreground">
                {note ||
                  (resent.length === sending.length && resent.length === 1
                  ? "We couldn’t confirm the booking. Wait a few minutes, then check Booked Trips: if this trip is there, you’re done."
                  : resent.length === 1
                    ? "We couldn’t confirm the booking. Wait a few minutes, then check this date in Booked Trips. If it has this trip, take it out with Edit on the Transport date step."
                    : resent.length === sending.length
                      ? "We couldn’t confirm the booking. Wait a few minutes, then check these dates in Booked Trips, and use Edit on the Transport date step to take out any that have this trip. If they all do, you’re done."
                      : "We couldn’t confirm the booking. Wait a few minutes, then check these dates in Booked Trips, and use Edit on the Transport date step to take out any that have this trip.") +
                  (tripType === "round_trip"
                    ? " Check the return trip too: if only one of the two is there, call dispatch."
                    : tripType === "multi_leg"
                      ? " Check every leg: if only some are there, call dispatch."
                      : "")}
              </p>
            </div>
          </div>
          {resent.length > 1 ? (
            <p className="mt-2 pl-6 text-xs font-medium text-foreground/90">
              {resent
                .map((d) => formatRecurringDate(d).replace(/ /gu, "\u00a0"))
                .join(" · ")}
            </p>
          ) : null}
          <label className="mt-2.5 flex cursor-pointer items-start gap-2 text-xs font-medium text-foreground">
            <Checkbox
              ref={holdCheckboxRef}
              checked={!unconfirmedBlocked}
              onCheckedChange={(checked) =>
                setValue(
                  "unconfirmed",
                  { ...held, checked: checked === true ? resent : [] },
                  { shouldDirty: true },
                )
              }
              className="mt-0.5 border-amber-700 dark:border-amber-400/60"
            />
            <span>
              {resent.length === 1
                ? `I’ve checked Booked Trips — this trip isn’t booked on ${formatRecurringDate(resent[0]!)}.`
                : "I’ve checked Booked Trips — this trip isn’t booked on any of these dates."}
            </span>
          </label>
        </div>
      ) : null}

      {submitError ? (
        <p
          role="alert"
          className="rounded-xl border border-destructive/50 bg-destructive/10 px-3 py-2 text-sm font-semibold text-destructive"
        >
          {submitError}
        </p>
      ) : null}

      <div className="flex flex-wrap items-center justify-between gap-2 pt-1">
        <div className="flex flex-wrap items-center gap-2">
          <Button type="button" variant="secondary" onClick={onBack}>
            <ArrowLeft className="h-4 w-4" /> Back
          </Button>
          <Button
            type="button"
            variant="ghost"
            onClick={async () => {
              const proceed = await confirm({
                title: "Reset the entire form?",
                description:
                  "Every field will be cleared and the patient lookup will start over. This can't be undone.",
                confirmText: "Reset form",
                cancelText: "Keep my work",
                destructive: true,
              });
              if (proceed) onResetAll();
            }}
          >
            <RotateCcw className="h-4 w-4" /> Reset form
          </Button>
        </div>
        <Button
          type="button"
          size="lg"
          onClick={onSubmit}
          disabled={submitting || !payload || duplicateBlocked || unconfirmedBlocked}
          className="min-w-[14rem]"
        >
          {submitting ? (
            <>
              <Spinner /> Sending request…
            </>
          ) : (
            <>
              <Send className="h-4 w-4" />{" "}
              {isRecurring
                ? `Submit ${repeatDates.length} trip${repeatDates.length === 1 ? "" : "s"}`
                : "Submit trip request"}
            </>
          )}
        </Button>
      </div>
    </WizardCard>
  );
}
